// Display formatting shared by both UIs. Kept out of any component so the
// Studio and the cockpit can format the same value the same way without
// importing each other's chrome -- formatMinor used to live in
// components/Nav.tsx, which made every view that showed a price depend on
// the navigation bar.

/** Money as Core stores it: integer minor units plus a currency tag
 * (08-contracts.md #1 `balance`/`quoted_total`). Only USD_CENTS exists
 * today (pricelist_dev); anything else falls through to a readable
 * "<amount> <CURRENCY>" rather than pretending to know its scale. */
export function formatMinor(amountMinor: number, currency: string): string {
  if (currency === "USD_CENTS") {
    return `$${(amountMinor / 100).toFixed(2)}`;
  }
  return `${amountMinor} ${currency}`;
}

/** Signed money, for ledger rows and balance adjustments, where the sign
 * carries the meaning and "-$5.00" beats "$-5.00". */
export function formatMinorSigned(amountMinor: number, currency: string): string {
  const sign = amountMinor < 0 ? "-" : "+";
  return sign + formatMinor(Math.abs(amountMinor), currency);
}

/** A usage record read as its effect on the account's balance.
 *
 * Core stores every amount as a positive magnitude and puts the direction
 * in `kind` (internal/core/pricing.go): a CHARGE is money gone, a REFUND
 * money back, and an ADJUSTMENT is the one kind that arrives already
 * signed, because an admin may credit or debit. Rendering the magnitude
 * with a fixed "+" -- which is what taking `amount_minor` at face value
 * does -- prints every charge and every cost as if it were a credit.
 *
 * A COST gets no sign at all rather than a wrong one: it is the
 * provider's bill to the platform, and 02-core.md #9 is explicit that it
 * is recorded "never charged to the user". */
export function ledgerAmount(kind: string, amountMinor: number, currency: string): string {
  // A free run (every echo alias is priced at zero) charges nothing, and
  // "-$0.00" reads as a defect rather than as a receipt. Zero has no
  // direction, so it gets no sign whichever kind it arrived under.
  if (amountMinor === 0) return formatMinor(0, currency);
  switch (kind.toUpperCase()) {
    case "CHARGE":
      return "-" + formatMinor(Math.abs(amountMinor), currency);
    case "REFUND":
      return "+" + formatMinor(Math.abs(amountMinor), currency);
    case "COST":
      return formatMinor(Math.abs(amountMinor), currency);
    default:
      return formatMinorSigned(amountMinor, currency);
  }
}

const WHEN = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

export function formatWhen(iso?: string | null): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  return WHEN.format(new Date(t));
}

/** Elapsed time since an ISO timestamp, coarse on purpose: this labels
 * "how long has this been idle/provisioning", not a stopwatch. */
export function formatSince(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const s = Math.max(0, Math.floor((now - t) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h${m % 60 ? ` ${m % 60}m` : ""}`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

/** "12 of 20" style counts for progress readouts. */
export function formatCount(done: number, total: number): string {
  return `${done} / ${total}`;
}
